import { type PopupComponentProps, type TriggerProps } from '@atlaskit/popup';
import { Box, xcss } from '@atlaskit/primitives';
import Popup, { type Placement } from '@databases/atlaskit/Popup';
import { getCloudId } from '@databases/database-state';
import usePanel from '@databases/panel/usePanel';
import { useResizeObserverCallback } from '@databases/react';
import { type DefaultRootState, defaultStoreUtils } from '@databases/redux';
import { deselectAll } from '@databases/redux/cellSelections/actions';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { DatabaseChangeType } from '@databases/types/redux';
import useHandleOutsideClick from '@databases/utils/hooks/useHandleOutsideClick';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import React, {
  forwardRef,
  Fragment,
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';

import { currentHostFrameContext } from '@canvas/canvas-state/host-api';
import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { type CommentThreadEvent } from '@canvas/comments/comment/store';
import { CommentContentType } from '@canvas/comments/comment-types';
import { getCommentStore } from '@canvas/comments/state/comment-store';
import { getNotificationStore } from '@canvas/comments/state/notification-store';
import { waitForContentAPI } from '@canvas/frame-api/host-api/host-frame-context';
import { currentDrawerPanel } from '@canvas/sidebar-panel-state';

import { type OpenThreadContextData } from '../context/CommentContext';
import { useCommentActionsContext } from '../hooks/useCommentContext';
import { type Thread } from '../type';

import CellCommentsButton from './CellCommentsButton';
import CommentThreads from './CommentThreads';
import FloatingCellActionButtons from './FloatingCellActionButtons';

const DATABASE_COMMENT_POPUP_OFFSET: [number, number] = [33, 9];
const PANEL_COMMENT_POPUP_OFFSET: [number, number] = [24, 15];
const FLOATING_ACTIONS_DB_COMMENT_POPUP_OFFSET: [number, number] = [45, 9];

const PANEL_COMMENT_PADDING = 70;
const FLOATING_ACTIONS_PANEL_COMMENT_HORIZONTAL_OFFSET = 32;

const FLOATING_ACTIONS_POPUP_VERTICAL_OFFSET = -2;
const THREADS_VERTICAL_OFFSET = -12;
const SINGLE_THREAD_HORIZONTAL_OFFSET = 59;
const THREADS_HORIZONTAL_OFFSET = 62;

const containerStyles = xcss({
  zIndex: 'dialog',
});

const commentsButtonStyles = xcss({
  padding: 'space.0',
  position: 'absolute',
  right: 'space.100',
  top: 'space.100',
});

const floatingActionsButtonStyles = xcss({
  position: 'absolute',
  right: '0',
  top: '0',
});

// eslint-disable-next-line react/display-name
const CustomPopupContainer = forwardRef<
  HTMLDivElement,
  Omit<PopupComponentProps, 'xcss'>
>(({ children, 'data-testid': testId, ...props }, ref) => (
  <Box xcss={containerStyles} testId={testId} {...props} ref={ref}>
    {children}
  </Box>
));

interface ThreadPopupProps {
  canEdit: boolean;
  contentId: string;
  entryId: string;
  fieldId: string;
  hasThreads: boolean;
  openThread: OpenThreadContextData;
  panel?: boolean;
  placement?: Placement;
  setIsHoverCell?: (isHovered: boolean) => void;
  setIsThreadPopupHovered: (isHovered: boolean) => void;
  shouldShowHoverButton: boolean;
  threads: Thread[] | undefined;
  toggleComment: (e: React.MouseEvent) => void;
  totalCommentCount: number;
  valueId: string;
}

const ThreadPopup: FunctionComponent<ThreadPopupProps> = ({
  canEdit,
  contentId,
  entryId,
  fieldId,
  hasThreads,
  openThread,
  panel = false,
  placement = 'right-start',
  setIsHoverCell,
  setIsThreadPopupHovered,
  shouldShowHoverButton,
  threads,
  toggleComment,
  totalCommentCount,
  valueId,
}) => {
  const { setOpenThread, setFocusedCommentThreadId } = useCommentActionsContext();
  const dispatch = defaultStoreUtils.useDispatch();
  const store = getCommentStore(undefined, currentHostFrameContext);
  const { showPanel } = usePanel();
  const notificationStore = getNotificationStore();
  const cloudId = getCloudId();
  const commentThreadsPopupRef = useRef<HTMLDivElement>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  const isVisualRefreshM1Enabled = isFeatureFlagEnabled(
    'databases-frontend-visual-refresh-table-m1'
  );
  const isCommentsPanelOpen = currentDrawerPanel.panel === 'commentsPanel';
  const firstCommentThreadId = threads?.[0]?.threadId;

  const isFirstField = defaultStoreUtils.useEqualSelector((state: DefaultRootState) => {
    const database = selectDatabaseByContentIdRequired(state, {
      contentId,
    });

    const fields = database.fields;
    return fieldId === fields[0];
  });

  const shouldUseFloatingActions =
    isVisualRefreshM1Enabled &&
    ((isFirstField && hasThreads && shouldShowHoverButton) ||
      (!hasThreads && shouldShowHoverButton));

  const handleExpandEntry = useCallback(() => {
    showPanel('EntryViewPanel', {
      entryId,
      isReadonly: !canEdit,
    });
  }, [canEdit, entryId, showPanel]);

  const commentToggled = useMemo(
    () => openThread.valueId === valueId && !!openThread.panel === panel,
    [openThread.panel, openThread.valueId, panel, valueId]
  );

  useEffect(() => {
    if (!commentToggled) {
      setIsThreadPopupHovered(false);
    }
  }, [commentToggled, setIsThreadPopupHovered]);

  const handleClose = useCallback(() => {
    setOpenThread({ valueId: undefined });
    setFocusedCommentThreadId(undefined);
    setIsHoverCell?.(false);
    dispatch(deselectAll());
  }, [dispatch, setFocusedCommentThreadId, setIsHoverCell, setOpenThread]);

  // Since hook is mounted on every cell, we only want to call the close method when the ref is valid and user is not in delete dialog
  useHandleOutsideClick(
    handleClose,
    commentThreadsPopupRef,
    [
      '#emoji-picker',
      `#cell-view-thread-button-${valueId}`,
      `#cell-add-thread-button-${valueId}`,
      `[aria-roledescription="comment-more-actions-dropdown"]`,
      `[aria-roledescription="comment-more-actions-dropdown-group"]`,
      '[data-cancel-thread-popup-outside-click="true"]',
    ], // ignore clicks on emoji button in "more emojis" menu and edit button
    commentToggled && !isDeleting
  );

  const rightSidePanel =
    panel && commentToggled ? document.getElementById('right-side-panel') : null;
  useResizeObserverCallback(rightSidePanel, forceUpdate);

  const getPanelOffset = (): [number, number] => {
    const el = document.getElementById('right-side-panel');

    if (!el) {
      return PANEL_COMMENT_POPUP_OFFSET;
    }
    const width = el.offsetWidth;
    if (!isVisualRefreshM1Enabled) {
      if (hasThreads) {
        return [THREADS_VERTICAL_OFFSET, width - 88];
      }
      return [THREADS_VERTICAL_OFFSET, width - PANEL_COMMENT_PADDING];
    }

    if (hasThreads && isFirstField) {
      return [
        FLOATING_ACTIONS_POPUP_VERTICAL_OFFSET,
        width - FLOATING_ACTIONS_PANEL_COMMENT_HORIZONTAL_OFFSET,
      ];
    }
    if (!hasThreads && shouldShowHoverButton) {
      return [
        FLOATING_ACTIONS_POPUP_VERTICAL_OFFSET,
        width - FLOATING_ACTIONS_PANEL_COMMENT_HORIZONTAL_OFFSET,
      ];
    }
    if (hasThreads && totalCommentCount === 1) {
      return [THREADS_VERTICAL_OFFSET, width - SINGLE_THREAD_HORIZONTAL_OFFSET];
    }
    return [THREADS_VERTICAL_OFFSET, width - THREADS_HORIZONTAL_OFFSET];
  };

  const getDBCommentPopupOffset = () => {
    if (shouldUseFloatingActions) {
      return FLOATING_ACTIONS_DB_COMMENT_POPUP_OFFSET;
    }
    return DATABASE_COMMENT_POPUP_OFFSET;
  };

  const onClick = useCallback(
    async (e: React.MouseEvent) => {
      if (isCommentsPanelOpen) {
        const contentAPI = await waitForContentAPI();
        contentAPI.confluenceAPI?.comments?.selectCommentThread?.(firstCommentThreadId);
      }
      toggleComment(e);

      if (firstCommentThreadId && isFeatureFlagEnabled('unreadComments')) {
        // Mark comment as read on load
        const threadFromStore = store.getThread(firstCommentThreadId);
        const comments =
          threadFromStore?.status === 'fetched' ? threadFromStore.comments : [];

        notificationStore.markCommentsAsViewed(
          comments
            ?.map((comment) => comment.id)
            .filter((comment) => comment !== undefined)
        );
      }
    },

    [isCommentsPanelOpen, toggleComment, firstCommentThreadId, notificationStore, store]
  );

  useEffect(() => {
    const subscriber = (evt: CommentThreadEvent) => {
      if (evt.type === 'threadNotFound') {
        dispatch(
          addChangeToDraft(contentId, {
            type: DatabaseChangeType.REMOVE_VALUE_COMMENT_THREAD,
            entryId,
            fieldId,
            threadId: evt.threadId,
          })
        );
      } else if (evt.type === 'threadLoaded') {
        const threadId = evt.threadId;
        const thread = threads?.find((thread) => thread?.threadId === threadId);
        if (
          thread &&
          evt.commentCount !== undefined &&
          thread.commentCount !== evt.commentCount
        ) {
          dispatch(
            addChangeToDraft(contentId, {
              type: DatabaseChangeType.SET_VALUE_COMMENT_THREAD_COMMENT_COUNT,
              entryId,
              fieldId,
              threadId: thread.threadId,
              commentCount: evt.commentCount,
            })
          );
        }
      }
    };

    store.subscribe(subscriber);

    // pre-fetch threads on mount to avoid a flash of loading state and to ensure that the comment count is correct
    threads?.map((thread) => {
      if (thread) {
        store.initializeThread(
          thread.threadId,
          thread.threadId,
          cloudId,
          contentId,
          CommentContentType.DATABASE
        );
      }
    });

    return () => store.unsubscribe(subscriber);
  }, [store, cloudId, contentId, dispatch, entryId, fieldId, threads]);

  const handleMouseEnter = useCallback(() => {
    setIsThreadPopupHovered(true);
  }, [setIsThreadPopupHovered]);

  const handleMouseLeave = useCallback(() => {
    setIsThreadPopupHovered(false);
  }, [setIsThreadPopupHovered]);

  const PopupContent = useCallback(() => {
    if (isCommentsPanelOpen && hasThreads) {
      return null;
    }

    return (
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        id="comment-threads-popup"
        ref={commentThreadsPopupRef}
      >
        <CommentThreads
          contentId={contentId}
          entryId={entryId}
          fieldId={fieldId}
          commentToggled={commentToggled}
          threads={threads}
          openThread={openThread}
          create={openThread.create}
          setIsDeleting={setIsDeleting}
          handleClose={handleClose}
        />
      </div>
    );
  }, [
    commentToggled,
    contentId,
    entryId,
    fieldId,
    handleClose,
    openThread,
    setIsDeleting,
    threads,
    isCommentsPanelOpen,
    hasThreads,
    handleMouseEnter,
    handleMouseLeave,
  ]);

  return (
    <Popup
      shouldRenderToParent={true}
      isOpen={commentToggled}
      placement={placement}
      autoFocus={false}
      boundary={document.getElementById('layout-table') ?? 'clippingParents'}
      popupComponent={CustomPopupContainer as FunctionComponent<PopupComponentProps>}
      offset={panel ? getPanelOffset() : getDBCommentPopupOffset()}
      contentContainer={Fragment}
      trigger={(triggerProps?: TriggerProps) => {
        if (!(hasThreads || shouldShowHoverButton)) {
          return null;
        }

        if (isVisualRefreshM1Enabled) {
          if (shouldUseFloatingActions) {
            return (
              <Box
                ref={triggerProps?.ref}
                id={`cell-comment-button-${valueId}`}
                xcss={floatingActionsButtonStyles}
              >
                <FloatingCellActionButtons
                  onCommentClick={onClick}
                  commentToggled={commentToggled}
                  hasThreads={hasThreads}
                  commentCount={totalCommentCount}
                  shouldShowHoverButton={shouldShowHoverButton}
                  valueId={valueId}
                  isHovered={shouldShowHoverButton}
                  isFirstField={isFirstField}
                  handleExpandEntry={handleExpandEntry}
                  panel={panel}
                />
              </Box>
            );
          } else {
            return (
              <Box
                ref={triggerProps?.ref}
                id={`cell-comment-button-${valueId}`}
                xcss={commentsButtonStyles}
              >
                <CellCommentsButton
                  onClick={onClick}
                  commentToggled={commentToggled}
                  hasThreads={hasThreads}
                  commentCount={totalCommentCount}
                  shouldShowHoverButton={shouldShowHoverButton}
                  valueId={valueId}
                />
              </Box>
            );
          }
        } else {
          return (
            <Box
              ref={triggerProps?.ref}
              id={`cell-comment-button-${valueId}`}
              xcss={commentsButtonStyles}
            >
              <CellCommentsButton
                onClick={onClick}
                commentToggled={commentToggled}
                hasThreads={hasThreads}
                commentCount={totalCommentCount}
                shouldShowHoverButton={shouldShowHoverButton}
                valueId={valueId}
              />
            </Box>
          );
        }
      }}
      content={PopupContent}
    />
  );
};

export default React.memo(ThreadPopup);
